import { FormLabel, Textinput, IValidateError, AppEmitter, APP_EMITTER_EVENTS } from "shared";
import { ITemplateInputComponent } from "entities/template";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useTranslation } from "react-i18next";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_input.module.scss";

interface IDocumentFormInputProps {
    formRecord: Record<string, any>;
    component: ITemplateInputComponent;
    onChange: (tag: string, value?: string) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormInput: React.FC<IDocumentFormInputProps> = React.memo(({
                                                                                    component,
                                                                                    formRecord,
                                                                                    label,
                                                                                    readonly,
                                                                                    tooltip,
                                                                                    labelShort,
                                                                                    triggerValidate,
                                                                                    onValidationRegister,
                                                                                    externalUpdateRecord,
                                                                                    onChange,
                                                                                }) => {
    const t = useComponentsTranslations();
    const [value, setValue] = useState<string>("");
    const { i18n } = useTranslation();

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true)
            }, component.name);
            onChange(component.name, undefined);
        }
    }, []);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, [])

    useEffect(() => {
        if (component.name) {
            onChange(component.name, value);
        }
    }, [value])

    useEffect(() => {
        onValidationRegister(validateInput, component.name)
    }, [component.name, formRecord])

    useEffect(() => {
        const updateVal = externalUpdateRecord[component.name];
        if (component.name) {
            setValue(updateVal ? updateVal : "");
        }
    }, [externalUpdateRecord, component.name])

    const onChangeValue = (val: string) => {
        setValue(val);
    }

    const handleClearEvent = () => {
        if (!readonly) {
            onChangeValue("");
        }
    }

    const validate = useMemo(() => {
        return component.required !== false ||
            !!component.maxNumber ||
            !!component.minNumber ||
            !!component.maxStringLength ||
            !!component.maxStringLength;
    }, [component])

    const required = useMemo(() => {
        return component.required !== false
    }, [component])

    const fieldType = useMemo(() => {
        return component.isNumber ? "number" : undefined;
    }, [component])

    const validateFunction = useCallback((value: string): IValidateError => {
        if (!value) {
            if (required) {
                return {
                    valid: false,
                    message: t("requiredFieldError")
                }
            } else {
                return {
                    valid: true
                }
            }
        }
        if (component.isNumber) {
            try {
                if (component.maxNumber && parseInt(value) > parseInt(component.maxNumber)) {
                    return {
                        valid: false,
                        message: t("greaterThanMaxNumber", { maxNumber: component.maxNumber })
                    }
                }
                if (component.minNumber && parseInt(value) < parseInt(component.minNumber)) {
                    return {
                        valid: false,
                        message: t("lessThanMinNumber", { minNumber: component.minNumber })
                    }
                }
            } catch (e) {
                return {
                    valid: false,
                    message: t("invalidValue")
                }
            }
        }
        if (component.maxStringLength && value.length > parseInt(component.maxStringLength)) {
            return {
                valid: false,
                message: t("maxStringLength", { maxStringLength: component.maxStringLength })
            }
        }
        if (component.minStringLength && value.length < parseInt(component.minStringLength)) {
            return {
                valid: false,
                message: t("minStringLength", { minStringLength: component.minStringLength })
            }
        }

        return {
            valid: true
        }
    }, [component, i18n.language])

    const validateInput = (): Promise<boolean> => {
        const valid = !required || validateFunction(value).valid;
        return Promise.resolve(valid);
    }

    return component.name ? <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <Textinput size="m"
                       id={`${component.type}_${component.name}`}
                       disabled={readonly}
                       containerClassName={styles.input}
                       type={fieldType}
                       validate={validate}
                       validateFunc={validateFunction}
                       triggerValidate={triggerValidate}
                       value={value}
                       onChange={(e) => onChangeValue(e.target.value ? e.target.value : "")}
                       view="outline"/>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel> : null;
});
